import { useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { can } from "@shared/domain";
import { cn } from "@/lib/cn";
import { useUser } from "@/lib/queries";
import { useCounts } from "./counts";
import { useUi } from "@/stores/ui";
import { Sheet, SheetClose } from "../overlays";
import { AddSquareIcon, HamburgerMenuIcon } from "../icons";
import { AccountMenuBody, ModeToggle } from "./Header";
import { areaHref, areaOf, navFor, type Area } from "./navItems";

const usePath = () => useRouterState({ select: (s) => s.location.pathname });

function Badge({
  count,
  className,
}: {
  count?: { n: number; alarm: boolean };
  className?: string;
}) {
  if (!count?.n) return null;
  return (
    <span
      className={cn(
        "inline-flex h-[18px] min-w-5 items-center justify-center rounded-full px-1.5 font-mono text-[10px]",
        count.alarm ? "bg-rhodo text-[#FFFDF8]" : "bg-line-strong text-ink",
        className,
      )}
    >
      {count.n}
    </span>
  );
}

function useAreaLinks() {
  const me = useUser();
  const last = useUi((s) => s.lastSection);
  const areas = navFor(me.role);
  return { areas, href: (a: Area) => areaHref(a, last) };
}

/**
 * The course bar. Navigation laid as a horizontal band under the header,
 * instead of a sidebar. The active course is lifted onto the surface and
 * carries a deodar lintel.
 */
export function CourseNav() {
  const path = usePath();
  const active = areaOf(path);
  const counts = useCounts();
  const { areas, href } = useAreaLinks();
  return (
    <nav
      aria-label="Areas"
      className="hidden items-center gap-2 border-b border-line bg-sunk px-3 py-1.5 min-[900px]:flex"
    >
      <div className="no-scrollbar flex min-w-0 flex-1 gap-[2px] overflow-x-auto">
        {areas.map((a, i) => {
          const on = a.id === active;
          const Icon = a.icon;
          return (
            <Link
              key={a.id}
              to={href(a)}
              aria-current={on ? "page" : undefined}
              className={cn(
                "group relative flex min-h-[42px] shrink-0 items-center gap-2 rounded-md px-3.5 text-[14px] no-underline transition-colors duration-100",
                on
                  ? "bg-surface font-semibold text-ink shadow-[inset_0_0_0_1px_var(--border)]"
                  : "text-muted hover:bg-surface hover:text-ink",
              )}
            >
              <span
                className={cn(
                  "absolute inset-x-3.5 top-0 h-[3px] rounded-b-[3px]",
                  on
                    ? "bg-deodar"
                    : "bg-transparent group-hover:bg-line-strong",
                )}
              />
              <span className="font-mono text-[10px] text-subtle">
                {String(i + 1).padStart(2, "0")}
              </span>
              <Icon size={18} className={on ? "text-deodar" : ""} />
              <span className="whitespace-nowrap">{a.label}</span>
              <Badge count={counts.area[a.id]} />
            </Link>
          );
        })}
      </div>
      <Link
        to="/new"
        className="kadil flex min-h-[42px] shrink-0 items-center gap-2 rounded-md bg-deodar px-[18px] text-[15px] font-bold text-on-fill no-underline hover:brightness-110"
      >
        <AddSquareIcon size={20} /> New booking
      </Link>
    </nav>
  );
}

/**
 * Phones: four areas around the New button, then More. A manager's day is
 * checks, repairs and the desk; staff work checks, repairs and laundry.
 * More holds the other areas and the account menu (Settings included).
 */
export function BottomBar() {
  const me = useUser();
  const path = usePath();
  const active = areaOf(path);
  const counts = useCounts();
  const { areas, href } = useAreaLinks();
  const [more, setMore] = useState(false);
  const bar: Area["id"][] = can(me, "upkeep.assign")
    ? ["today", "checks", "repairs", "desk"]
    : ["today", "checks", "repairs", "laundry"];
  const rest = areas.filter((a) => !bar.includes(a.id));

  const tab = (id: Area["id"]) => {
    const a = areas.find((x) => x.id === id);
    if (!a) return null;
    const on = active === id;
    const Icon = a.icon;
    return (
      <Link
        key={a.id}
        to={href(a)}
        className={cn(
          "relative flex min-h-[58px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] no-underline",
          on ? "font-semibold text-ink" : "text-muted",
        )}
      >
        <span
          className={cn(
            "absolute inset-x-3 top-0 h-[3px] rounded-b-[3px]",
            on ? "bg-deodar" : "bg-transparent",
          )}
        />
        <Icon size={22} className={on ? "text-deodar" : ""} />
        <span className="max-w-full truncate px-0.5">
          {a.label === a.staffLabel ? a.label : (a.short ?? a.label)}
        </span>
        <Badge
          count={counts.area[a.id]}
          className="absolute right-1.5 top-1.5"
        />
      </Link>
    );
  };

  // Running a check, the sticky submit bar takes the bottom of the screen.
  if (/^\/checks\/tasks\/[^/]+\/run/.test(path)) return null;
  const moreOn =
    more || path.startsWith("/settings") || rest.some((a) => a.id === active);

  return (
    <>
      <nav
        aria-label="Areas"
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line-strong bg-surface pb-[env(safe-area-inset-bottom)] min-[900px]:hidden"
      >
        {tab(bar[0])}
        {tab(bar[1])}
        <Link
          to="/new"
          aria-label="New booking"
          className="flex flex-[1.1] items-center justify-center no-underline"
        >
          <span className="-mt-4 flex size-[58px] flex-col items-center justify-center gap-0.5 rounded-full bg-deodar text-[11px] font-bold text-on-fill shadow-float">
            <AddSquareIcon size={22} />
            New
          </span>
        </Link>
        {tab(bar[2])}
        {tab(bar[3])}
        <button
          onClick={() => setMore(true)}
          className={cn(
            "relative flex min-h-[58px] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 text-[11px]",
            moreOn ? "font-semibold text-ink" : "text-muted",
          )}
        >
          <span
            className={cn(
              "absolute inset-x-3 top-0 h-[3px] rounded-b-[3px]",
              moreOn ? "bg-deodar" : "bg-transparent",
            )}
          />
          <HamburgerMenuIcon
            size={22}
            className={moreOn ? "text-deodar" : ""}
          />
          More
        </button>
      </nav>

      <Sheet open={more} onClose={() => setMore(false)} label="More">
        <div className="flex items-center justify-between px-4 pb-2">
          <span className="font-display text-[20px] font-semibold">More</span>
          <SheetClose onClose={() => setMore(false)} />
        </div>
        <div className="overflow-y-auto overscroll-contain pb-[calc(12px+env(safe-area-inset-bottom))]">
          {rest.length > 0 && (
            <div className="grid grid-cols-3 gap-2 px-4 pb-4">
              {rest.map((a) => {
                const Icon = a.icon;
                const on = active === a.id;
                return (
                  <Link
                    key={a.id}
                    to={href(a)}
                    onClick={() => setMore(false)}
                    className={cn(
                      "relative flex min-h-[76px] flex-col items-center justify-center gap-1.5 rounded-md border px-1.5 text-center text-[13px] leading-tight no-underline",
                      on
                        ? "border-deodar bg-deodar-tint font-semibold text-ink"
                        : "border-line-strong bg-surface text-ink",
                    )}
                  >
                    <Icon
                      size={22}
                      className={on ? "text-deodar" : "text-muted"}
                    />
                    {a.label}
                    <Badge
                      count={counts.area[a.id]}
                      className="absolute right-1.5 top-1.5"
                    />
                  </Link>
                );
              })}
            </div>
          )}
          <div className="mx-4 rounded-lg border border-line-strong bg-surface py-3">
            <AccountMenuBody onNavigate={() => setMore(false)} big />
          </div>
          <div className="flex items-center justify-end px-4 pt-3">
            <ModeToggle />
          </div>
        </div>
      </Sheet>
    </>
  );
}
